import { IProfitPreviewDTO } from "@hooks/dtos/profit/IProfitPreviewDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { KeyboardEvent, useState } from "react";
import {
  FinancialProjectionInfoButton,
  FinancialProjectionInfoKey,
  FinancialProjectionInfoModal,
} from "./FinancialProjectionInfoButton";

type FinancialProjectionExtractProps = {
  ticketAmount: number;
  ticketPrice: number;
  rewardPrice: number;
  preview?: IProfitPreviewDTO;
  isLoading: boolean;
  error?: string;
  showGrossRevenue?: boolean;
};

const masks = new Masks();

export function FinancialProjectionExtract({
  ticketAmount,
  ticketPrice,
  rewardPrice,
  preview,
  isLoading,
  error,
  showGrossRevenue = false,
}: FinancialProjectionExtractProps) {
  const [selectedInfo, setSelectedInfo] =
    useState<FinancialProjectionInfoKey>();

  function getInfoRowProps(item: FinancialProjectionInfoKey) {
    return {
      role: "button",
      tabIndex: 0,
      style: { cursor: "pointer" },
      title: "Clique para entender este item.",
      onClick: () => setSelectedInfo(item),
      onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setSelectedInfo(item);
        }
      },
    };
  }

  const grossRevenue = ticketAmount * ticketPrice;
  const purchase = preview?.purchase;
  const ticketSell = preview?.ticketSell;
  const campaignPurchaseCost =
    (purchase?.factor || 0) +
    (purchase?.price || 0) +
    (purchase?.externalFee || 0);
  const externalSalesFees = ticketSell?.externalFeeTotal || 0;
  const platformSalesFees =
    (ticketSell?.platform.partner || 0) + (ticketSell?.platform.fees || 0);
  const afterRewards = grossRevenue - rewardPrice;
  const afterExternalFees = afterRewards - externalSalesFees;
  const afterTicketSalesCosts = afterExternalFees - platformSalesFees;
  const netProfit = afterTicketSalesCosts - campaignPurchaseCost;
  const balanceClass = (value: number) => (value >= 0 ? "success" : "danger");

  return (
    <>
      <div className="row text-center">
        <div className="card-header">
          <div className="card-title">Resumo Financeiro</div>
          <div className="card-category">
            Entradas, taxas internas e externas, lucro bruto e líquido, etc...
          </div>
        </div>
      </div>

      <div className="row my-4">
        <div className="col-md-11 mx-auto rounded-4 border">
          <div className="card-header text-center">
            <div className="fs-5">Suas Configurações</div>
            <div className="m-0 p-0 text-muted fs-6">
              <small>Informações previamente preenchidas</small>
            </div>
          </div>
          <div className="card-body border-bottom">
            <div className="row d-flex justify-content-around text-center">
              <div
                className={`col-md-12 ${showGrossRevenue ? "col-xxl-3" : "col-xxl-4"}`}
              >
                <span className="fs-4 fw-bold">
                  {masks.formatMilhar(ticketAmount)}
                </span>
                <br />
                <span>Quantidade de Tickets</span>
              </div>
              <div
                className={`col-md-12 ${showGrossRevenue ? "col-xxl-3" : "col-xxl-4"}`}
              >
                <span className="fs-4 fw-bold">
                  {masks.brlCurrency(ticketPrice)}
                </span>
                <br />
                <span>Preço Unitário do Ticket</span>
              </div>
              {showGrossRevenue ? (
                <div className="col-md-12 col-xxl-3">
                  <span className="fs-4 fw-bold">
                    {masks.brlCurrency(grossRevenue)}
                  </span>
                  <br />
                  <span>Arrecadação Total (Bruta)</span>
                </div>
              ) : null}
              <div
                className={`col-md-12 ${showGrossRevenue ? "col-xxl-3" : "col-xxl-4"}`}
              >
                <span className="fs-4 fw-bold">
                  {masks.brlCurrency(rewardPrice)}
                </span>
                <br />
                <span>Valor do(s) Prêmio(s)</span>
              </div>
            </div>
          </div>
          <div className="p-1 text-end text-muted">
            <small>Resumo das suas configurações desta página.</small>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-11 mx-auto rounded-4 border">
          <div className="card-header text-center">
            <div className="fs-5">Preço de Aquisição da Campanha</div>
            <div className="m-0 p-0 text-muted fs-6">
              <small>Precificação para aquisição da Campanha.</small>
            </div>
          </div>
          <div className="card-body border-bottom">
            <div className="row d-flex justify-content-between">
              <div className="col-12 text-center">
                <span className="fs-4 fw-bold">
                  {isLoading
                    ? "Atualizando..."
                    : masks.brlCurrency(campaignPurchaseCost)}
                </span>
                <br />
                <span>Preço Final</span>
              </div>
            </div>
          </div>
          <div className="p-1 text-end text-muted">
            <small>Preço total para a aquisição da Campanha.</small>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-11 mx-auto rounded-4 border">
          <div className="card-header text-center">
            <div className="fs-5">Projeção Financeira</div>
            <div className="m-0 p-0 text-muted fs-6">
              <small>Estimativa de Lucro Líquido</small>
            </div>
          </div>

          {error ? (
            <div className="alert alert-warning mx-3 mb-0" role="alert">
              {error}
            </div>
          ) : null}

          <div className="table-responsive">
            <table className="table table-hover caption-top mb-0">
              <thead>
                <tr>
                  <th scope="col">Objeto</th>
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                  <th scope="col" className="text-end">
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr {...getInfoRowProps("revenue")}>
                  <th scope="row">
                    <span className="d-inline-flex align-items-center gap-2">
                      <FinancialProjectionInfoButton
                        item="revenue"
                        onOpen={() => setSelectedInfo("revenue")}
                      />
                      Arrecadação Estimada
                    </span>
                  </th>
                  <td className="fs-5 text-success text-end">
                    {masks.brlCurrency(grossRevenue)}
                  </td>
                  <td className="fs-5 text-success text-end">
                    {masks.brlCurrency(grossRevenue)}
                  </td>
                </tr>
                <tr {...getInfoRowProps("rewards")}>
                  <th scope="row">
                    <span className="d-inline-flex align-items-center gap-2">
                      <FinancialProjectionInfoButton
                        item="rewards"
                        onOpen={() => setSelectedInfo("rewards")}
                      />
                      Valor Total em Prêmio(s)
                    </span>
                  </th>
                  <td className="fs-5 text-warning text-end">
                    {masks.brlCurrency(rewardPrice)}
                  </td>
                  <td
                    className={`fs-5 text-${balanceClass(afterRewards)} text-end`}
                  >
                    {masks.brlCurrency(afterRewards)}
                  </td>
                </tr>
                <tr {...getInfoRowProps("externalFees")}>
                  <th scope="row">
                    <span className="d-inline-flex align-items-center gap-2">
                      <FinancialProjectionInfoButton
                        item="externalFees"
                        onOpen={() => setSelectedInfo("externalFees")}
                      />
                      Taxas Externas
                    </span>
                  </th>
                  <td className="fs-5 text-warning text-end">
                    {masks.brlCurrency(externalSalesFees)}
                  </td>
                  <td
                    className={`fs-5 text-${balanceClass(afterExternalFees)} text-end`}
                  >
                    {masks.brlCurrency(afterExternalFees)}
                  </td>
                </tr>
                <tr {...getInfoRowProps("platformFees")}>
                  <th scope="row">
                    <span className="d-inline-flex align-items-center gap-2">
                      <FinancialProjectionInfoButton
                        item="platformFees"
                        onOpen={() => setSelectedInfo("platformFees")}
                      />
                      Taxas da Plataforma
                    </span>
                  </th>
                  <td className="fs-5 text-warning text-end">
                    {masks.brlCurrency(platformSalesFees)}
                  </td>
                  <td
                    className={`fs-5 text-${balanceClass(afterTicketSalesCosts)} text-end`}
                  >
                    {masks.brlCurrency(afterTicketSalesCosts)}
                  </td>
                </tr>
                <tr {...getInfoRowProps("campaignPurchase")}>
                  <th scope="row">
                    <span className="d-inline-flex align-items-center gap-2">
                      <FinancialProjectionInfoButton
                        item="campaignPurchase"
                        onOpen={() => setSelectedInfo("campaignPurchase")}
                      />
                      Aquisição da Campanha
                    </span>
                  </th>
                  <td className="fs-5 text-warning text-end">
                    {masks.brlCurrency(campaignPurchaseCost)}
                  </td>
                  <td
                    className={`fs-5 text-${balanceClass(netProfit)} text-end`}
                  >
                    {masks.brlCurrency(netProfit)}
                  </td>
                </tr>
                <tr {...getInfoRowProps("netProfit")}>
                  <th scope="row" colSpan={2}>
                    <span className="d-inline-flex align-items-center gap-2">
                      <FinancialProjectionInfoButton
                        item="netProfit"
                        onOpen={() => setSelectedInfo("netProfit")}
                      />
                      Lucro Líquido Estimado
                    </span>
                  </th>
                  <td
                    className={`fs-5 text-${balanceClass(netProfit)} text-end`}
                  >
                    {isLoading
                      ? "Atualizando..."
                      : masks.brlCurrency(netProfit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <FinancialProjectionInfoModal
            item={selectedInfo}
            onClose={() => setSelectedInfo(undefined)}
          />
          <div className="d-flex flex-column text-end mb-2">
            <small className="text-muted">
              * Seu lucro líquido pode variar caso adicione afiliados
              comissionados à sua Campanha.
            </small>
          </div>
        </div>
      </div>
    </>
  );
}

